<template>
  <RouterView
    v-slot="{ Component }"
    :hash-code="hashCode"
    :result-id="resultId"
    :active-offer="activeOffer"
    :all-offers="allOffers"
    @onOfferSelect="handleOfferSelect"
    @onSuccessfulSearch="handleSuccessfulSearch"
    @onFailedSearch="handleFailedSearch"
  >
    <!-- Cache only ResultsPage to preserve filters state -->
    <KeepAlive include="ResultsPage">
      <component :is="Component" :dict="dict" />
    </KeepAlive>
  </RouterView>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import { RouterView } from 'vue-router';
  import type { flightEntity } from 'freedom-shared';
  import type { Dict } from '@aviata/platform-types';

  import { ancillaryBaggageModel } from '@/widgets/ancillary-baggage';

  import { analyticsEventsLib } from '@/shared/lib';

  defineProps<{ hashCode: string }>();

  const activeOffer = ref<flightEntity.types.MappedOffer | null>(null);
  const allOffers = ref<flightEntity.types.MappedOffer[]>([]);
  const dict = ref<Dict.Entity | null>(null);
  const resultId = ref<string>();

  const ancillariesStore = ancillaryBaggageModel.useAncillariesStore();

  const handleOfferSelect = (offer: flightEntity.types.MappedOffer) => {
    activeOffer.value = offer;
  };

  const handleSuccessfulSearch = (
    newDict: Dict.Entity,
    newResultId: string,
    dataForAnalytics: analyticsEventsLib.SearchPageOpenParams,
  ) => {
    dict.value = newDict;
    resultId.value = newResultId;
    /**
     * Need to send events here, since it the only correct way.
     * If send events on ResultsPage, then it can be sent after polling is finished even if user is on main page already
     * and won't see the ResultsPage.
     *  */
    analyticsEventsLib.searchPageOpen(dataForAnalytics);
  };

  const handleFailedSearch = (
    dataForAnalytics: analyticsEventsLib.SearchPageOpenParams,
  ) => {
    analyticsEventsLib.searchPageOpen(dataForAnalytics);
  };

  /**
   * Need to reset ancillaries store here,
   * because this is the only page where offer id can be changed
   * */
  onMounted(() => ancillariesStore.$reset());
</script>
